import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./Navbar";

const EMAIL = "vmdpproject@gmail.com";
const UPDATED = "10 luglio 2026";
const ACCENT = "#10b981";

/* ─── Sezione testo legale ─── */
function Section({ n, title, children }) {
  return (
    <div
      style={{
        marginBottom: "2.75rem",
        paddingBottom: "2.75rem",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "1rem",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            fontWeight: 500,
            color: ACCENT,
            background: "rgba(16,185,129,0.1)",
            border: "1px solid rgba(16,185,129,0.28)",
            borderRadius: "6px",
            padding: "3px 9px",
            flexShrink: 0,
          }}
        >
          {String(n).padStart(2, "0")}
        </span>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(1.3rem, 2.5vw, 1.6rem)",
            color: "var(--color-text)",
            letterSpacing: "-0.01em",
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
      <div
        style={{
          color: "var(--color-text-2)",
          fontSize: "1rem",
          lineHeight: 1.85,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const linkStyle = {
  color: ACCENT,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
};
const ulStyle = {
  paddingLeft: "1.25rem",
  margin: "0.75rem 0 0",
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};
const strong = { color: "var(--color-text)" };

export default function VeyroTerms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Stesso documento servito sotto /veyro e /veyro-trainer: i link
     di ritorno seguono l'app da cui si arriva. */
  const { pathname } = useLocation();
  const base = pathname.startsWith("/veyro-trainer") ? "/veyro-trainer" : "/veyro";
  const appName = base === "/veyro-trainer" ? "Veyro Trainer" : "Veyro";

  return (
    <>
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: "var(--color-bg)", minHeight: "100vh" }}
      >
        {/* ── Hero ── */}
        <section
          style={{
            paddingTop: "68px",
            background:
              "linear-gradient(180deg, rgba(16,185,129,0.08) 0%, transparent 100%)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <div className="container" style={{ padding: "4rem 1.75rem 3rem" }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: ACCENT,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: "1rem",
              }}
            >
              {`// ${appName.toLowerCase()} · termini e condizioni`}
            </span>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                fontSize: "clamp(2.2rem, 5vw, 3.2rem)",
                letterSpacing: "-0.03em",
                lineHeight: 1.1,
                marginBottom: "1rem",
                color: "var(--color-text)",
              }}
            >
              Termini e Condizioni
            </h1>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: "var(--color-muted)",
              }}
            >
              Veyro · Veyro Trainer · Ultimo aggiornamento: {UPDATED}
            </p>
          </div>
        </section>

        {/* ── Contenuto ── */}
        <div className="container" style={{ padding: "3rem 1.75rem 5rem" }}>
          {/* Intro card */}
          <div
            style={{
              background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.22)",
              backdropFilter: "blur(12px)",
              borderRadius: "var(--radius-lg)",
              padding: "1.5rem 1.75rem",
              marginBottom: "3rem",
              color: "var(--color-text-2)",
              fontSize: "1rem",
              lineHeight: 1.8,
            }}
          >
            <strong style={strong}>Leggere attentamente.</strong> L'uso delle
            applicazioni Veyro e Veyro Trainer comporta l'accettazione integrale
            dei presenti Termini e Condizioni. Le App sono attualmente in fase di{" "}
            <strong style={strong}>beta test</strong> (TestFlight iOS / Google
            Play Internal Testing) e non sostituiscono il parere di un medico o
            di un professionista della salute.
          </div>

          <Section n={1} title="Descrizione del Servizio">
            Veyro è un marketplace mobile di personal training sviluppato da
            VMDProject, disponibile in due applicazioni:{" "}
            <strong style={strong}>Veyro</strong> per i clienti (schede di
            allenamento, piani alimentari, progressi, accesso al marketplace dei
            personal trainer) e{" "}
            <strong style={strong}>Veyro Trainer</strong> per i personal trainer
            (gestione clienti, creazione schede e piani alimentari, vendita di
            pacchetti e coaching).
          </Section>

          <Section n={2} title="Fase Beta">
            Le App sono attualmente distribuite in{" "}
            <strong style={strong}>fase di beta test</strong> tramite TestFlight
            (iOS) e Google Play Internal Testing (Android). Funzionalità, dati e
            disponibilità del servizio possono variare o essere interrotti senza
            preavviso durante questa fase. VMDProject non garantisce l'assenza di
            bug o malfunzionamenti nella versione beta.
          </Section>

          <Section n={3} title="Accettazione dei Termini">
            Scaricando, installando o utilizzando Veyro o Veyro Trainer accettate
            integralmente i presenti Termini e Condizioni, nonché la nostra{" "}
            <Link to={`${base}/policy`} style={linkStyle}>
              Privacy Policy
            </Link>
            . Se non accettate questi termini, non utilizzate l'applicazione.
            L'utilizzo è consentito esclusivamente a persone fisiche di età pari
            o superiore a 16 anni.
          </Section>

          <Section n={4} title="Account e Registrazione">
            <ul style={ulStyle}>
              <li>
                La creazione di un account richiede un indirizzo email valido (o
                login Apple/Google) e una password sicura.
              </li>
              <li>
                Siete responsabili della riservatezza delle credenziali di
                accesso e di tutte le attività effettuate tramite il vostro
                account.
              </li>
              <li>Ogni account è personale e non trasferibile.</li>
              <li>
                VMDProject si riserva il diritto di sospendere o eliminare
                account che violino i presenti Termini.
              </li>
            </ul>
          </Section>

          <Section n={5} title="Marketplace, Abbonamenti e Pagamenti">
            <ul style={ulStyle}>
              <li>
                Veyro offre un piano gratuito e un abbonamento Pro (€2,99/mese)
                con schede illimitate e analytics avanzati.
              </li>
              <li>
                Il marketplace consente l'acquisto di schede una tantum o
                coaching mensile da personal trainer verificati, con split
                payment automatico gestito tramite Stripe Connect (il personal
                trainer riceve l'85% dell'importo).
              </li>
              <li>
                I pagamenti sono elaborati tramite Stripe o gli store
                Apple/Google, secondo il flusso di acquisto. VMDProject non
                accede direttamente ai dati completi della carta.
              </li>
              <li>
                Gli abbonamenti si rinnovano automaticamente al termine del
                periodo, salvo disdetta.
              </li>
              <li>
                Non sono previsti rimborsi per periodi già iniziati, salvo
                diversa disposizione di legge.
              </li>
            </ul>
          </Section>

          <Section n={6} title="Avvertenze Sanitarie">
            <div
              style={{
                background: "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.28)",
                borderRadius: "var(--radius-lg)",
                padding: "1.25rem 1.5rem",
                marginBottom: "1rem",
              }}
            >
              <strong style={{ color: "#fbbf24" }}>⚠️ Avviso importante</strong>
              <p style={{ margin: "0.5rem 0 0", color: "var(--color-text-2)" }}>
                Veyro è uno strumento di supporto al fitness e non costituisce
                consulenza medica, nutrizionale o terapeutica. Prima di iniziare
                qualsiasi programma di allenamento o piano alimentare,
                consultate il vostro medico, in particolar modo in presenza di
                patologie cardiache, ortopediche o metaboliche.
              </p>
            </div>
            VMDProject declina ogni responsabilità per infortuni, danni alla
            salute o conseguenze derivanti dall'uso dei programmi di allenamento
            o dei piani alimentari forniti dall'applicazione o dai personal
            trainer del marketplace.
          </Section>

          <Section n={7} title="Ruolo dei Personal Trainer">
            I personal trainer presenti sul marketplace sono soggetti terzi
            indipendenti, non dipendenti di VMDProject. VMDProject verifica le
            informazioni fornite in fase di registrazione ma non garantisce la
            qualità, l'accuratezza o l'idoneità professionale dei servizi di
            coaching erogati. Eventuali controversie tra cliente e personal
            trainer relative al servizio di coaching sono di esclusiva competenza
            delle parti coinvolte.
          </Section>

          <Section n={8} title="Responsabilità dell'Utente">
            Utilizzando Veyro l'utente si impegna a:
            <ul style={ulStyle}>
              <li>
                Fornire informazioni accurate sul proprio stato di salute e
                livello di forma fisica.
              </li>
              <li>
                Non caricare contenuti illegali, offensivi o che violino i
                diritti di terzi.
              </li>
              <li>
                Non tentare di accedere in modo non autorizzato ai sistemi o agli
                account di altri utenti.
              </li>
              <li>
                Non utilizzare l'applicazione per scopi commerciali non previsti
                dal marketplace, senza autorizzazione scritta.
              </li>
            </ul>
          </Section>

          <Section n={9} title="Obblighi del Personal Trainer">
            Il personal trainer che utilizza Veyro Trainer si impegna a:
            <ul style={ulStyle}>
              <li>
                Fornire dati identificativi e professionali veritieri e, per il
                livello Certificato, certificazioni autentiche e verificabili.
              </li>
              <li>
                Erogare i servizi venduti nel marketplace nei tempi e nei modi
                dichiarati nel pacchetto pubblicato.
              </li>
              <li>
                Completare l'onboarding Stripe Connect per ricevere i payout; la
                fatturazione e gli obblighi fiscali sui compensi restano a carico
                del personal trainer.
              </li>
              <li>
                Non richiedere ai clienti pagamenti al di fuori della piattaforma
                per servizi originati dal marketplace.
              </li>
            </ul>
          </Section>

          <Section n={10} title="Contenuti e Proprietà Intellettuale">
            Tutti i contenuti delle applicazioni Veyro e Veyro Trainer — inclusi
            schede di allenamento predefinite, libreria esercizi, testi,
            immagini, video e il codice sorgente — sono di esclusiva proprietà di
            VMDProject o dei suoi licenziatari. I contenuti creati dai personal
            trainer (schede personalizzate, piani alimentari) rimangono di
            proprietà del personal trainer che li ha creati, fatta salva la
            licenza d'uso concessa a VMDProject per l'erogazione del servizio.
          </Section>

          <Section n={11} title="Limitazione di Responsabilità">
            Nei limiti consentiti dalla legge applicabile, VMDProject non sarà
            responsabile per:
            <ul style={ulStyle}>
              <li>
                Danni diretti o indiretti derivanti dall'uso o dall'impossibilità
                di utilizzo dell'applicazione, inclusa la fase beta.
              </li>
              <li>
                Interruzioni del servizio per manutenzione programmata o cause di
                forza maggiore.
              </li>
              <li>
                Perdita di dati dovuta a malfunzionamenti del dispositivo
                dell'utente.
              </li>
              <li>Risultati di allenamento o nutrizionali non raggiunti.</li>
            </ul>
          </Section>

          <Section n={12} title="Modifiche al Servizio e ai Termini">
            VMDProject si riserva il diritto di modificare, aggiornare o
            interrompere il servizio in qualsiasi momento, così come di
            aggiornare i presenti Termini. Le modifiche sostanziali saranno
            comunicate tramite notifica in-app o via email. L'uso continuato
            dell'applicazione dopo la pubblicazione delle modifiche costituisce
            accettazione dei nuovi Termini.
          </Section>

          <Section n={13} title="Risoluzione del Contratto">
            L'utente può eliminare il proprio account in qualsiasi momento
            tramite le impostazioni dell'app o scrivendo a{" "}
            <a href={`mailto:${EMAIL}`} style={linkStyle}>
              {EMAIL}
            </a>
            . VMDProject può risolvere il contratto e sospendere l'accesso in
            caso di violazione dei presenti Termini.
          </Section>

          <Section n={14} title="Legge Applicabile e Foro Competente">
            I presenti Termini sono regolati dalla legge italiana. Per qualsiasi
            controversia derivante dall'utilizzo di Veyro è competente in via
            esclusiva il Tribunale di Napoli, salvo diversa previsione
            obbligatoria di legge a favore del consumatore.
          </Section>

          <Section n={15} title="Contatti">
            Per qualsiasi domanda relativa ai presenti Termini e Condizioni:
            <br />
            <br />
            <strong style={strong}>VMDProject</strong> — Napoli, Italia
            <br />
            Email:{" "}
            <a href={`mailto:${EMAIL}`} style={linkStyle}>
              {EMAIL}
            </a>
          </Section>
        </div>

        {/* ─── Footer ─── */}
        <footer
          style={{
            borderTop: "1px solid var(--color-border)",
            padding: "2rem 0",
          }}
        >
          <div
            className="container"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <Link
              to={base}
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "var(--color-text)",
                textDecoration: "none",
              }}
            >
              ← {appName}
            </Link>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              <Link
                to={`${base}/policy`}
                style={{
                  fontSize: "0.9rem",
                  color: "var(--color-muted)",
                  textDecoration: "none",
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "var(--color-text)")}
                onMouseLeave={(e) => (e.target.style.color = "var(--color-muted)")}
              >
                Privacy
              </Link>
              <Link
                to={`${base}/terms`}
                style={{ fontSize: "0.9rem", color: ACCENT, textDecoration: "none" }}
              >
                Termini
              </Link>
            </div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: "var(--color-muted)",
              }}
            >
              © {new Date().getFullYear()} VMDProject
            </span>
          </div>
        </footer>
      </motion.main>
    </>
  );
}
